/**
 * Lightweight wrapper around the global `window.Telegram.WebApp` API loaded
 * from https://telegram.org/js/telegram-web-app.js. We intentionally avoid
 * the heavier @telegram-apps/sdk-react state model since our UI state lives
 * in Zustand and our server state lives in TanStack Query.
 */

export type HapticImpact = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotification = 'error' | 'success' | 'warning';

function tg() {
  return typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
}

export function isInTelegram(): boolean {
  const w = tg();
  if (!w) return false;
  return !!w.initDataUnsafe?.user?.id;
}

function syncSafeAreaVars() {
  const w = tg();
  const root = document.documentElement;
  if (!w) return;
  // Telegram WebApp Bot API 8.0+
  const sa = (w as unknown as { safeAreaInset?: Record<string, number> })
    .safeAreaInset;
  const csa = (
    w as unknown as { contentSafeAreaInset?: Record<string, number> }
  ).contentSafeAreaInset;
  if (sa) {
    root.style.setProperty('--tg-safe-area-top', `${sa.top ?? 0}px`);
    root.style.setProperty('--tg-safe-area-bottom', `${sa.bottom ?? 0}px`);
    root.style.setProperty('--tg-safe-area-left', `${sa.left ?? 0}px`);
    root.style.setProperty('--tg-safe-area-right', `${sa.right ?? 0}px`);
  }
  if (csa) {
    root.style.setProperty('--tg-content-safe-area-top', `${csa.top ?? 0}px`);
    root.style.setProperty(
      '--tg-content-safe-area-bottom',
      `${csa.bottom ?? 0}px`,
    );
  }
}

export function initTelegram() {
  const w = tg();
  if (!w) return;
  try {
    w.ready?.();
  } catch {
    /* ignore */
  }
  try {
    w.expand?.();
  } catch {
    /* ignore */
  }
  try {
    w.disableVerticalSwipes?.();
  } catch {
    /* ignore */
  }
  // Don't requestFullscreen — it hides system safe-area and overlaps content
  syncSafeAreaVars();
  try {
    const wAny = w as unknown as {
      onEvent?: (event: string, handler: () => void) => void;
    };
    wAny.onEvent?.('safe_area_changed', syncSafeAreaVars);
    wAny.onEvent?.('content_safe_area_changed', syncSafeAreaVars);
    wAny.onEvent?.('viewport_changed', syncSafeAreaVars);
    wAny.onEvent?.('theme_changed', applyTheme);
  } catch {
    /* ignore */
  }
  applyTheme();
}

export function applyTheme() {
  const w = tg();
  if (!w) return;
  const params = w.themeParams ?? {};
  const root = document.documentElement;
  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === 'string') {
      root.style.setProperty(`--tg-${key.replace(/_/g, '-')}`, value);
    }
  });
  if (w.colorScheme) {
    root.dataset.tgScheme = w.colorScheme;
  }
}

export function showBackButton(handler: () => void): () => void {
  const w = tg();
  if (!w?.BackButton) return () => undefined;
  w.BackButton.onClick(handler);
  w.BackButton.show();
  return () => {
    try {
      w.BackButton?.offClick(handler);
      w.BackButton?.hide();
    } catch {
      /* ignore */
    }
  };
}

export function hideBackButton() {
  const w = tg();
  try {
    w?.BackButton?.hide();
  } catch {
    /* ignore */
  }
}

export function haptic(style: HapticImpact = 'light') {
  try {
    tg()?.HapticFeedback?.impactOccurred(style);
  } catch {
    /* ignore */
  }
}

export function hapticNotify(type: HapticNotification) {
  try {
    tg()?.HapticFeedback?.notificationOccurred(type);
  } catch {
    /* ignore */
  }
}

export function getViewportHeight(): number {
  return tg()?.viewportStableHeight ?? window.innerHeight;
}

export function getPlatform(): string {
  return tg()?.platform ?? 'web';
}
